/**
 * Pagina do Pipeline de Aprovacao (Admin)
 * Route: /dashboard/pipeline
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Card, CardContent, CardHeader, CardTitle,
  Button, Input, Badge, useToast,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  SkeletonTable,
  ScoreBadge, ScoreDetailsDialog,
  type BadgeVariant,
} from "@payflow/ui";
import {
  getClientScore,
  getClientScoresBatch,
  type ClientScoreResponse,
} from "../../services/clients.service";

const API_BASE = (import.meta as any).env?.VITE_API_URL || "http://localhost:8787";
const TOKEN_KEY = "payflow_token";

// ============ Types ============

type PipelineStatus =
  | "docs_pendentes" | "aguardando_reenvio" | "pendente_reanalise"
  | "docs_aprovados" | "contrato_enviado"
  | "contrato_assinado" | "pix_pendente" | "pix_enviado" | "recusado";

interface PipelineItem {
  id: string;
  clienteId: string;
  leadId: string | null;
  emprestimoId: string | null;
  contratoId: string | null;
  status: PipelineStatus;
  valor: string;
  parcelas: number;
  frequencia: string;
  taxaJuros: string;
  observacoes: string | null;
  recusadoMotivo: string | null;
  createdAt: string;
  updatedAt: string;
  clienteNome: string | null;
  clienteCpf: string | null;
  clienteTelefone: string | null;
  clienteWhatsapp?: string | null;
  clienteChavePix?: string | null;
  clienteTipoChavePix?: string | null;
  campanhaNome?: string | null;
  campanhaCodigo?: string | null;
  clienteReferencias?: Array<{ nome: string; telefone: string; relacao?: string }>;
  documents?: DocumentItem[];
  contract?: {
    id: string;
    status: string;
    canalEnvio: string | null;
    providerRequestId: string | null;
  } | null;
}

interface DocumentItem {
  id: string;
  campo: string;
  tipo: string;
  storageKey: string;
  fileUrl?: string;
  status: string;
  motivoReprovacao: string | null;
  versao: number;
  // Backend flag: existe alguma versao anterior (soft-deletada ou nao) que
  // foi reprovada pelo admin. So true nesse caso vale mostrar "Reanalise" —
  // versao > 1 sozinho nao basta (cliente refaz captura no proprio
  // onboarding e ja cria v2).
  hasRejectedHistory?: boolean;
}

type TabFilter = "todos" | PipelineStatus | "bloqueado";

const STATUS_CONFIG: Record<PipelineStatus, { label: string; color: string; badge: BadgeVariant }> = {
  docs_pendentes: { label: "Análise Pendente", color: "bg-yellow-100 text-yellow-800", badge: "warning" },
  aguardando_reenvio: { label: "Aguardando Reenvio", color: "bg-rose-100 text-rose-800", badge: "warning" },
  pendente_reanalise: { label: "Pendente de Reanálise", color: "bg-amber-100 text-amber-800", badge: "warning" },
  docs_aprovados: { label: "Docs Aprovados", color: "bg-blue-100 text-blue-800", badge: "info" },
  contrato_enviado: { label: "Contrato Enviado", color: "bg-indigo-100 text-indigo-800", badge: "info" },
  contrato_assinado: { label: "Contrato Assinado", color: "bg-purple-100 text-purple-800", badge: "default" },
  pix_pendente: { label: "PIX Pendente", color: "bg-orange-100 text-orange-800", badge: "warning" },
  pix_enviado: { label: "PIX Enviado", color: "bg-green-100 text-green-800", badge: "success" },
  recusado: { label: "Recusado", color: "bg-red-100 text-red-800", badge: "error" },
};

const FREQ_LABELS: Record<string, string> = {
  diario: "Diario",
  semanal: "Semanal",
  quinzenal: "Quinzenal",
  mensal: "Mensal",
};

/**
 * Calcula a data de 1o vencimento default a partir da frequencia do plano.
 * Regra: proximo periodo — diario=+1d, semanal=+7d, quinzenal=+14d, mensal=+1m.
 * Usa componentes locais pra evitar drift de timezone (a noite no Brasil o UTC
 * ja e o dia seguinte e +24h caia 2 dias adiante).
 */
function defaultVencimentoByFreq(frequencia: string | undefined): string {
  const d = new Date();
  switch (frequencia) {
    case "semanal":
      d.setDate(d.getDate() + 7);
      break;
    case "quinzenal":
      d.setDate(d.getDate() + 14);
      break;
    case "mensal":
      d.setMonth(d.getMonth() + 1);
      break;
    case "diario":
    default:
      d.setDate(d.getDate() + 1);
      break;
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const KANBAN_COLUMNS: PipelineStatus[] = [
  "docs_pendentes", "aguardando_reenvio", "pendente_reanalise",
  "docs_aprovados", "contrato_enviado",
  "contrato_assinado", "pix_pendente", "pix_enviado",
];

// ============ API ============

async function apiFetch(path: string, options?: RequestInit) {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers || {}),
    },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.error?.message || "Erro na requisicao");
  return data;
}

// ============ Component ============

/**
 * `mode="renovacoes"` reusa toda a tela pra rota /dashboard/renovacoes.
 * Muda:
 *  - Titulo + subtitulo
 *  - Query com ?origem=renovacao (ou solicitacao)
 *  - Tabs escondem "docs_pendentes"/"docs_aprovados" (renovacao nao passa
 *    por analise de documentos — o cliente ja e cadastrado).
 *  - Default activeTab vai pro primeiro passo real (contrato_enviado).
 */
export interface PipelinePageProps {
  mode?: "solicitacoes" | "renovacoes";
}

export function PipelinePage({ mode = "solicitacoes" }: PipelinePageProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const isRenewals = mode === "renovacoes";
  const [items, setItems] = useState<PipelineItem[]>([]);
  const [_total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabFilter>(
    isRenewals ? "contrato_enviado" : "docs_pendentes",
  );

  // Detail modal
  const [detailItem, setDetailItem] = useState<PipelineItem | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [clientData, setClientData] = useState<Record<string, unknown> | null>(null);
  // Rotula docs pelo label do FlowEditor (mesmo padrao do Cadastro app em
  // /dashboard/clientes). Se nao houver label configurado, cai no
  // humanizeKey. campo -> label.
  const [docLabels, setDocLabels] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!detailItem?.clienteId) {
      setClientData(null);
      setDocLabels({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [clientResp, labelsResp] = await Promise.all([
          apiFetch(`/v1/admin/clients/${detailItem.clienteId}`),
          apiFetch(`/v1/admin/clients/${detailItem.clienteId}/onboarding-labels`).catch(() => ({ data: {} })),
        ]);
        if (cancelled) return;
        setClientData(clientResp.data || null);
        const meta = (labelsResp.data || {}) as Record<string, { label?: string }>;
        const labels: Record<string, string> = {};
        for (const [k, v] of Object.entries(meta)) {
          if (v?.label) labels[k] = v.label;
        }
        setDocLabels(labels);
      } catch {
        if (!cancelled) {
          setClientData(null);
          setDocLabels({});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [detailItem?.clienteId]);

  // Converte "document_upload_4" em "Document Upload 4" quando nao ha
  // label do flow. Copia direta do helper que ClientsPage usa.
  const humanizeKey = (key: string): string =>
    key
      .replace(/[_-]+/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  // Reject modal
  const [rejectItem, setRejectItem] = useState<PipelineItem | null>(null);
  const [rejectMotivo, setRejectMotivo] = useState("");
  const [rejecting, setRejecting] = useState(false);

  const [blockItem, setBlockItem] = useState<PipelineItem | null>(null);
  const [blockMotivo, setBlockMotivo] = useState("");
  const [blocking, setBlocking] = useState(false);

  // Confirm PIX modal
  const [pixItem, setPixItem] = useState<PipelineItem | null>(null);
  // Inicial = amanha (sem contexto de plano ainda). Ao abrir o modal com um
  // pixItem, ajustamos pra respeitar a frequencia do plano (semanal=+7,
  // mensal=+1m). Ver useEffect abaixo.
  const [pixDate, setPixDate] = useState(() => defaultVencimentoByFreq("diario"));
  const [confirmingPix, setConfirmingPix] = useState(false);

  // Snapshot do pixDate inicial pra prompt de alteracoes nao salvas.
  const pixInitialDateRef = useRef<string>("");

  // Reset da data quando abre o modal, respeitando a frequencia do plano.
  useEffect(() => {
    if (pixItem) {
      const initial = defaultVencimentoByFreq(pixItem.frequencia);
      setPixDate(initial);
      pixInitialDateRef.current = initial;
    }
  }, [pixItem?.id, pixItem?.frequencia]);

  // Score modal (agora com dados reais via GET /clients/:id/score)
  const [scoreItem, setScoreItem] = useState<PipelineItem | null>(null);
  const [scoreDetails, setScoreDetails] = useState<ClientScoreResponse | null>(null);
  const [scoreLoading, setScoreLoading] = useState(false);

  // Document preview / reject modal
  const [previewDoc, setPreviewDoc] = useState<DocumentItem | null>(null);
  const [rejectDoc, setRejectDoc] = useState<DocumentItem | null>(null);
  const [rejectDocMotivo, setRejectDocMotivo] = useState("");
  const [docActionLoading, setDocActionLoading] = useState(false);

  // Score real por cliente (batch — evita N+1)
  const [scoreByClient, setScoreByClient] = useState<Record<string, number>>({});

  const loadPipeline = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = activeTab !== "todos" ? `&status=${activeTab}` : "";
      const origemParam = `&origem=${isRenewals ? "renovacao" : "solicitacao"}`;
      const data = await apiFetch(`/v1/admin/pipeline?page=1&limit=100${statusParam}${origemParam}`);
      const rows = (data.data || []) as PipelineItem[];
      setItems(rows);
      setTotal(data.meta?.total || 0);
      const ids = Array.from(new Set(rows.map((r) => r.clienteId).filter(Boolean)));
      if (ids.length > 0) {
        try {
          const scores = await getClientScoresBatch(ids);
          setScoreByClient((prev) => ({ ...prev, ...scores }));
        } catch {
          // silencioso — cai no mock via ScoreBadge
        }
      }
    } catch (err: any) {
      toast({ variant: "error", title: "Erro" });
    } finally {
      setLoading(false);
    }
  }, [activeTab, isRenewals, toast]);

  // Loan config (apenas o flag de exigir contrato — define quais botoes aparecem)
  const [exigirContrato, setExigirContrato] = useState(true);
  useEffect(() => {
    apiFetch("/v1/admin/loan-config")
      .then((d) => setExigirContrato(d.data?.contratoExigirAssinatura ?? true))
      .catch(() => setExigirContrato(true));
  }, []);

  useEffect(() => {
    loadPipeline();
  }, [loadPipeline]);

  useEffect(() => {
    if (!scoreItem) return;
    let cancelled = false;
    setScoreDetails(null);
    setScoreLoading(true);
    getClientScore(scoreItem.clienteId)
      .then((res) => {
        if (cancelled) return;
        setScoreDetails(res);
        setScoreByClient((prev) => ({ ...prev, [scoreItem.clienteId]: res.score }));
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setScoreLoading(false); });
    return () => { cancelled = true; };
  }, [scoreItem]);

  // Detail
  const openDetail = async (item: PipelineItem) => {
    setDetailLoading(true);
    setDetailItem(item);
    try {
      const data = await apiFetch(`/v1/admin/pipeline/${item.id}`);
      setDetailItem(data.data);
    } catch (err: any) {
      toast({ variant: "error", title: "Erro" });
    } finally {
      setDetailLoading(false);
    }
  };

  // Refresh detail after document action
  const refreshDetail = async () => {
    if (!detailItem) return;
    try {
      const data = await apiFetch(`/v1/admin/pipeline/${detailItem.id}`);
      setDetailItem(data.data);
    } catch { /* ignore */ }
  };

  const handleApproveDoc = async (docId: string) => {
    setDocActionLoading(true);
    try {
      await apiFetch(`/v1/admin/documents/${docId}/approve`, { method: "PUT" });
      toast({ variant: "success", title: "Documento aprovado" });
      await refreshDetail();
    } catch (err: any) {
      toast({ variant: "error", title: err.message || "Erro ao aprovar" });
    } finally {
      setDocActionLoading(false);
    }
  };

  const handleUnapproveDoc = async (docId: string) => {
    setDocActionLoading(true);
    try {
      await apiFetch(`/v1/admin/documents/${docId}/unapprove`, { method: "PUT" });
      toast({ variant: "success", title: "Aprovação desfeita" });
      await refreshDetail();
    } catch (err: any) {
      toast({ variant: "error", title: err.message || "Erro ao desfazer aprovação" });
    } finally {
      setDocActionLoading(false);
    }
  };

  const handleRejectDoc = async () => {
    if (!rejectDoc || !rejectDocMotivo.trim()) return;
    setDocActionLoading(true);
    try {
      await apiFetch(`/v1/admin/documents/${rejectDoc.id}/reject`, {
        method: "PUT",
        body: JSON.stringify({ motivo: rejectDocMotivo }),
      });
      toast({ variant: "success", title: "Documento reprovado" });
      setRejectDoc(null);
      setRejectDocMotivo("");
      await refreshDetail();
    } catch (err: any) {
      toast({ variant: "error", title: err.message || "Erro ao reprovar" });
    } finally {
      setDocActionLoading(false);
    }
  };

  const handleApproveAllDocs = async () => {
    if (!detailItem?.documents) return;
    const pendingIds = detailItem.documents.filter((d) => d.status === "pendente").map((d) => d.id);
    if (pendingIds.length === 0) return;
    setDocActionLoading(true);
    try {
      await apiFetch("/v1/admin/documents/bulk-approve", {
        method: "POST",
        body: JSON.stringify({ ids: pendingIds }),
      });
      toast({ variant: "success", title: `${pendingIds.length} documento(s) aprovado(s)` });
      await refreshDetail();
    } catch (err: any) {
      toast({ variant: "error", title: err.message || "Erro na aprovacao em lote" });
    } finally {
      setDocActionLoading(false);
    }
  };

  // Actions
  const handleAdvance = async (id: string, targetStatus?: PipelineStatus) => {
    try {
      await apiFetch(`/v1/admin/pipeline/${id}/advance`, {
        method: "PUT",
        body: JSON.stringify({ targetStatus }),
      });
      toast({ variant: "success", title: "Sucesso" });
      setDetailItem(null);
      loadPipeline();
    } catch (err: any) {
      toast({ variant: "error", title: err?.message || "Erro" });
    }
  };

  // Preview + Send contract flow
  const [contractPreviewItem, setContractPreviewItem] = useState<PipelineItem | null>(null);
  const [contractPreviewLoading, setContractPreviewLoading] = useState(false);
  const [contractPreviewPdf, setContractPreviewPdf] = useState<string | null>(null);
  const [contractPreviewId, setContractPreviewId] = useState<string | null>(null);
  const [contractSending, setContractSending] = useState(false);

  // Reenviar contrato
  const [resendItem, setResendItem] = useState<PipelineItem | null>(null);
  const [resendChannel, setResendChannel] = useState<"WHATSAPP" | "EMAIL">("WHATSAPP");
  const [resendMessage, setResendMessage] = useState("");
  const [resending, setResending] = useState(false);
  // Snapshot do estado inicial (canal + mensagem) pra prompt de alteracoes nao salvas.
  const resendInitialRef = useRef<{ channel: "WHATSAPP" | "EMAIL"; message: string }>({
    channel: "WHATSAPP",
    message: "",
  });

  const loadContractPreview = async (pipelineId: string, regenerate = false) => {
    setContractPreviewLoading(true);
    setContractPreviewPdf(null);
    try {
      const data = await apiFetch(`/v1/admin/contracts/from-pipeline/${pipelineId}/preview`, {
        method: "POST",
        body: JSON.stringify({ regenerate }),
      });
      setContractPreviewPdf(data.data.pdfBase64);
      setContractPreviewId(data.data.contract.id);
      if (regenerate) {
        toast({ variant: "success", title: t("pipeline.preview.generated") });
      }
    } catch (err: any) {
      toast({ variant: "error", title: err?.message || t("pipeline.preview.error") });
      setContractPreviewItem(null);
    } finally {
      setContractPreviewLoading(false);
    }
  };

  const handleOpenContractPreview = (item: PipelineItem) => {
    setContractPreviewItem(item);
    setContractPreviewPdf(null);
    setContractPreviewId(null);
    loadContractPreview(item.id);
  };

  const handleCloseContractPreview = () => {
    setContractPreviewItem(null);
    setContractPreviewPdf(null);
    setContractPreviewId(null);
  };

  const handleConfirmSendContract = async () => {
    if (!contractPreviewId) return;
    setContractSending(true);
    try {
      // Canal resolvido no backend via tenant config (Settings > Integracoes > AssineMais)
      await apiFetch(`/v1/admin/contracts/${contractPreviewId}/send`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      toast({ variant: "success", title: t("pipeline.preview.sent") });
      handleCloseContractPreview();
      setDetailItem(null);
      loadPipeline();
    } catch (err: any) {
      toast({ variant: "error", title: err?.message || t("pipeline.preview.send_error") });
    } finally {
      setContractSending(false);
    }
  };

  const handleOpenResend = (item: PipelineItem) => {
    setResendItem(item);
    // Prefill com canal atual do contrato (se houver), senao WhatsApp
    const canal = item.contract?.canalEnvio;
    const ch: "WHATSAPP" | "EMAIL" = canal === "EMAIL" ? "EMAIL" : "WHATSAPP";
    setResendChannel(ch);
    setResendMessage("");
    resendInitialRef.current = { channel: ch, message: "" };
  };

  const handleConfirmResend = async () => {
    if (!resendItem?.contract?.id) return;
    setResending(true);
    try {
      await apiFetch(`/v1/admin/contracts/${resendItem.contract.id}/resend`, {
        method: "POST",
        body: JSON.stringify({
          channel: resendChannel,
          ...(resendMessage.trim() ? { message: resendMessage.trim() } : {}),
        }),
      });
      toast({ variant: "success", title: t("pipeline.resend.success") });
      setResendItem(null);
      setResendMessage("");
      setDetailItem(null);
      loadPipeline();
    } catch (err: any) {
      toast({ variant: "error", title: err?.message || t("pipeline.resend.error") });
    } finally {
      setResending(false);
    }
  };

  const handleBlock = async () => {
    if (!blockItem?.clienteId) return;
    setBlocking(true);
    try {
      await apiFetch(`/v1/admin/clients/${blockItem.clienteId}/block`, {
        method: "POST",
        body: JSON.stringify(blockMotivo.trim() ? { motivo: blockMotivo.trim() } : {}),
      });
      toast({ variant: "success", title: t("pipeline.block.success") });
      setBlockItem(null);
      setBlockMotivo("");
      setDetailItem(null);
      loadPipeline();
    } catch (err: any) {
      toast({ variant: "error", title: err?.message || t("pipeline.block.error") });
    } finally {
      setBlocking(false);
    }
  };

  const handleReject = async () => {
    if (!rejectItem || !rejectMotivo.trim()) return;
    setRejecting(true);
    try {
      await apiFetch(`/v1/admin/pipeline/${rejectItem.id}/reject`, {
        method: "PUT",
        body: JSON.stringify({ motivo: rejectMotivo }),
      });
      toast({ variant: "success", title: "Sucesso" });
      setRejectItem(null);
      setRejectMotivo("");
      setDetailItem(null);
      loadPipeline();
    } catch (err: any) {
      toast({ variant: "error", title: "Erro" });
    } finally {
      setRejecting(false);
    }
  };

  const handleConfirmPix = async () => {
    if (!pixItem) return;
    setConfirmingPix(true);
    try {
      await apiFetch(`/v1/admin/pipeline/${pixItem.id}/confirm-pix`, {
        method: "POST",
        body: JSON.stringify({ dataInicioVencimento: pixDate }),
      });
      toast({ variant: "success", title: "Sucesso" });
      setPixItem(null);
      setDetailItem(null);
      loadPipeline();
    } catch (err: any) {
      toast({ variant: "error", title: "Erro" });
    } finally {
      setConfirmingPix(false);
    }
  };

  const formatCurrency = (val: string | null | undefined) => {
    if (val === null || val === undefined || val === "") return "—";
    const n = parseFloat(val);
    if (!Number.isFinite(n)) return "—";
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n);
  };

  const formatCpf = (cpf: string | null) =>
    cpf ? cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4") : "-";

  const getFileUrl = (doc: DocumentItem) => {
    return doc.fileUrl || "";
  };

  // Status counts
  const statusCounts: Record<string, number> = {};
  items.forEach((i) => {
    statusCounts[i.status] = (statusCounts[i.status] || 0) + 1;
  });

  const getActionsForStatus = (item: PipelineItem) => {
    // tone semantico: success = acao positiva/aprovar; warning = recusar
    // (reversivel); danger = destrutivo (apagar/bloquear); default = neutro.
    const actions: { label: string; onClick: () => void; tone?: "success" | "warning" | "danger" }[] = [];
    const s = item.status;

    if (s === "docs_pendentes" || s === "pendente_reanalise") {
      actions.push({ label: t("pipeline.action.approve_docs"), onClick: () => handleAdvance(item.id, "docs_aprovados"), tone: "success" });
    }
    if (s === "docs_aprovados") {
      if (exigirContrato) {
        actions.push({
          label: t("pipeline.action.generate_contract"),
          onClick: () => handleOpenContractPreview(item),
          tone: "success",
        });
        actions.push({ label: t("pipeline.action.skip_contract"), onClick: () => handleAdvance(item.id, "pix_pendente") });
      } else {
        actions.push({
          label: t("pipeline.action.advance_to_pix"),
          onClick: () => handleAdvance(item.id, "pix_pendente"),
          tone: "success",
        });
      }
    }
    if (s === "contrato_enviado" && item.contract?.id) {
      actions.push({
        label: t("pipeline.action.resend_contract"),
        onClick: () => handleOpenResend(item),
      });
    }
    if (s === "contrato_assinado") {
      actions.push({ label: t("pipeline.action.confirm_pix"), onClick: () => setPixItem(item), tone: "success" });
    }
    if (s === "pix_pendente") {
      actions.push({ label: t("pipeline.action.confirm_pix"), onClick: () => setPixItem(item), tone: "success" });
    }
    if (s !== "pix_enviado" && s !== "recusado") {
      actions.push({
        label: t("pipeline.action.reject"),
        onClick: () => { setRejectItem(item); setRejectMotivo(""); },
        tone: "warning",
      });
    }
    actions.push({
      label: t("pipeline.action.block"),
      onClick: () => { setBlockItem(item); setBlockMotivo(""); },
      tone: "danger",
    });
    return actions;
  };

  // Classe CSS por tone. Sobrepoe o variant do Button pra ter cor semantica
  // consistente (verde/amarelo/vermelho) sem depender dos tokens do design
  // system que nao expoem success/warning.
  const toneClass = (tone?: string): string => {
    if (tone === "success") return "!bg-green-600 !text-white hover:!bg-green-700 !border-green-600";
    if (tone === "warning") return "!bg-yellow-500 !text-white hover:!bg-yellow-600 !border-yellow-500";
    if (tone === "danger") return "!bg-red-600 !text-white hover:!bg-red-700 !border-red-600";
    return "";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {isRenewals ? "Renovações" : "Linha de Aprovação"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isRenewals
              ? "Acompanhe e gerencie as renovações solicitadas pelos clientes"
              : "Acompanhe e gerencie o fluxo de aprovação dos empréstimos"}
          </p>
        </div>
      </div>

      {/* Status filter tabs */}
      <div className="flex flex-wrap gap-1 rounded-lg border border-border bg-muted p-1">
          {([...KANBAN_COLUMNS, "recusado", "bloqueado"] as TabFilter[])
            // Renovacao pula analise de documentos — cliente ja e cadastrado.
            .filter((tab) => !isRenewals || (
              tab !== "docs_pendentes" &&
              tab !== "aguardando_reenvio" &&
              tab !== "pendente_reanalise" &&
              tab !== "docs_aprovados"
            ))
            .map((tab) => {
            const cfg = STATUS_CONFIG[tab as PipelineStatus];
            // Filtro "Solicitacoes" abre o mesmo bucket que docs_pendentes
            // mas o rotulo do filtro e diferente do status na linha
            // (status = "Analise Pendente").
            const tabLabel = tab === "docs_pendentes"
              ? "Solicitações"
              : tab === "bloqueado"
                ? t("pipeline.tab.blocked")
                : cfg?.label;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                  activeTab === tab
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {tabLabel}
              </button>
            );
          })}
      </div>

      {loading ? (
        <SkeletonTable rows={5} cols={6} />
      ) : (
        /* Table View */
        <Card>
          <CardContent className="p-0">
            {items.length === 0 ? (
              <div className="flex h-40 items-center justify-center text-muted-foreground">
                Nenhum item no pipeline
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Cliente</th>
                      <th className="px-3 py-3 text-center text-xs font-medium uppercase text-muted-foreground w-16">Score</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Campanha</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Valor</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Plano</th>
                      {activeTab === "pix_pendente" && (
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">PIX</th>
                      )}
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Data</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Acoes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const cfg = STATUS_CONFIG[item.status];
                      const actions = getActionsForStatus(item);
                      return (
                        <tr
                          key={item.id}
                          className="cursor-pointer border-b border-border hover:bg-muted/30"
                          onClick={() => openDetail(item)}
                        >
                          <td className="px-4 py-3">
                            <div className="font-medium text-foreground">{item.clienteNome || "Sem nome"}</div>
                            <div className="text-xs text-muted-foreground">{formatCpf(item.clienteCpf)}</div>
                          </td>
                          <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                            <ScoreBadge
                              cpf={item.clienteCpf}
                              score={scoreByClient[item.clienteId]}
                              onClick={() => setScoreItem(item)}
                            />
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {item.campanhaNome ? (
                              <>
                                <div className="font-medium text-foreground">{item.campanhaNome}</div>
                                {item.campanhaCodigo && (
                                  <div className="text-xs text-muted-foreground">{item.campanhaCodigo}</div>
                                )}
                              </>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium">{formatCurrency(item.valor)}</td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">
                            {item.parcelas ? (
                              <>
                                {item.parcelas}x {FREQ_LABELS[item.frequencia] || item.frequencia}
                                <br />
                                <span className="text-xs">
                                  {item.taxaJuros ? `${item.taxaJuros}% juros` : "—"}
                                </span>
                              </>
                            ) : (
                              <span className="text-xs">—</span>
                            )}
                          </td>
                          {activeTab === "pix_pendente" && (
                            <td className="px-4 py-3 text-sm" onClick={(e) => e.stopPropagation()}>
                              {item.clienteChavePix ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(item.clienteChavePix || "");
                                    toast({ variant: "success", title: "Chave PIX copiada" });
                                  }}
                                  className="max-w-[220px] text-left hover:underline"
                                  title="Clique pra copiar"
                                >
                                  <div className="truncate font-medium text-foreground">{item.clienteChavePix}</div>
                                  {item.clienteTipoChavePix && (
                                    <div className="text-xs text-muted-foreground">{item.clienteTipoChavePix}</div>
                                  )}
                                </button>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </td>
                          )}
                          <td className="px-4 py-3">
                            <Badge variant={cfg.badge} dot size="md">{cfg.label}</Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">
                            {new Date(item.createdAt).toLocaleDateString("pt-BR")}
                          </td>
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <div className="flex flex-wrap gap-1">
                              {actions.map((a, i) => (
                                <Button
                                  key={i}
                                  size="sm"
                                  variant="outline"
                                  className={toneClass(a.tone)}
                                  onClick={a.onClick}
                                >
                                  {a.label}
                                </Button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Detail Modal */}
      {detailItem && (
        <Dialog open={!!detailItem} onOpenChange={() => setDetailItem(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Detalhe do Empréstimo</DialogTitle>
            </DialogHeader>
            {detailLoading ? (
              <div className="flex h-32 items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
              </div>
            ) : (
              <div className="space-y-6">
                {detailItem.status === "recusado" && (
                  <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
                    <strong>Recusado:</strong> {detailItem.recusadoMotivo}
                  </div>
                )}

                {/* Valores do emprestimo (primeiro pra dar contexto imediato do valor) */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Valores do empréstimo</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(() => {
                      const valorNum = parseFloat(detailItem.valor) || 0;
                      const taxaPerPeriodo = parseFloat(detailItem.taxaJuros) || 0;
                      const parcelasNum = detailItem.parcelas || 0;
                      const hasTaxa = Number.isFinite(taxaPerPeriodo) && taxaPerPeriodo > 0;
                      const jurosEfetivo = hasTaxa ? taxaPerPeriodo * parcelasNum : 0;
                      const valorTotal = hasTaxa ? valorNum * (1 + jurosEfetivo / 100) : valorNum;
                      const valorParcela = parcelasNum > 0 ? valorTotal / parcelasNum : 0;
                      return (
                        <div className="grid grid-cols-2 gap-x-6 gap-y-5">
                          <Field
                            label="Valor"
                            value={formatCurrency(detailItem.valor)}
                            size="lg"
                          />
                          <Field
                            label="Parcelas"
                            value={
                              parcelasNum > 0
                                ? `${parcelasNum} x ${formatCurrency(valorParcela.toFixed(2))}`
                                : "-"
                            }
                            size="lg"
                          />
                          <Field
                            label="Juros"
                            value={hasTaxa ? `${jurosEfetivo.toFixed(0)}%` : "-"}
                            size="lg"
                          />
                          {/* Total a receber: mesma estrutura do Field, so o
                              valor ganha um badge verde pra chamar a atencao. */}
                          <div className="flex flex-col gap-1">
                            <span className="text-xs uppercase tracking-wide text-muted-foreground">
                              Total a receber
                            </span>
                            <span className="inline-flex w-fit items-center rounded-md bg-green-600 px-2.5 py-1 text-lg font-bold text-white">
                              {hasTaxa ? formatCurrency(valorTotal.toFixed(2)) : "-"}
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>

                {/* Dados Pessoais */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Dados Pessoais</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-5">
                      <Field label="Nome" value={(clientData?.nome as string) || detailItem.clienteNome || "-"} />
                      <Field label="CPF" value={formatCpf((clientData?.cpf as string) || detailItem.clienteCpf)} />
                      <Field label="Data de Nascimento" value={(clientData?.dataNascimento as string) || "-"} />
                      <Field label="RG" value={(clientData?.rg as string) || "-"} />
                      <Field label="Nacionalidade" value={(clientData?.nacionalidade as string) || "-"} />
                      <Field label="Estado Civil" value={(clientData?.estadoCivil as string) || "-"} />
                      <Field label="Profissão" value={(clientData?.profissao as string) || "-"} />
                    </div>
                  </CardContent>
                </Card>

                {/* Contato */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Contato</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-5">
                      <PhoneField
                        label="Telefone"
                        phone={(clientData?.telefone as string) || detailItem.clienteTelefone}
                      />
                      <PhoneField
                        label="WhatsApp"
                        phone={(clientData?.whatsapp as string) || detailItem.clienteWhatsapp}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Endereço */}
                {clientData && Boolean(clientData.cep || clientData.endereco || clientData.cidade) && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Endereço</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-5">
                        <Field label="CEP" value={(clientData.cep as string) || "-"} />
                        <Field label="Logradouro" value={(clientData.endereco as string) || "-"} />
                        <Field label="Número" value={(clientData.numero as string) || "-"} />
                        <Field label="Complemento" value={(clientData.complemento as string) || "-"} />
                        <Field label="Bairro" value={(clientData.bairro as string) || "-"} />
                        <Field label="Cidade" value={(clientData.cidade as string) || "-"} />
                        <Field label="Estado" value={(clientData.estado as string) || "-"} />
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* PIX */}
                {clientData && Boolean(clientData.chavePix || clientData.tipoChavePix) && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Chave PIX</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-5">
                        <Field label="Tipo" value={(clientData.tipoChavePix as string) || "-"} />
                        <Field label="Chave" value={(clientData.chavePix as string) || "-"} />
                      </div>
                    </CardContent>
                  </Card>
                )}

                {detailItem.observacoes && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Observações</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{detailItem.observacoes}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Referencias (contatos de emergencia) */}
                {detailItem.clienteReferencias && detailItem.clienteReferencias.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">
                        Referências ({detailItem.clienteReferencias.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {detailItem.clienteReferencias.map((ref, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-3 text-sm"
                          >
                            <div>
                              <div className="font-medium text-foreground">{ref.nome || "-"}</div>
                              {ref.relacao && (
                                <div className="text-xs text-muted-foreground">{ref.relacao}</div>
                              )}
                            </div>
                            <WhatsAppLink phone={ref.telefone} />

                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Documents */}
                {detailItem.documents && detailItem.documents.length === 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Documentos</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="py-6 text-center text-sm text-muted-foreground">
                        Nenhum documento enviado pelo cliente ainda
                      </p>
                    </CardContent>
                  </Card>
                )}
                {detailItem.documents && detailItem.documents.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm">
                          Documentos ({detailItem.documents.length})
                        </CardTitle>
                        {detailItem.documents.some((d) => d.status === "pendente") && (
                          <Button
                            size="sm"
                            onClick={handleApproveAllDocs}
                            disabled={docActionLoading}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            Aprovar todos pendentes
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {detailItem.documents.map((doc) => {
                          const statusColor = doc.status === "aprovado"
                            ? "bg-green-100 text-green-700 border-green-300"
                            : doc.status === "reprovado"
                              ? "bg-red-100 text-red-700 border-red-300"
                              : "bg-yellow-100 text-yellow-700 border-yellow-300";
                          return (
                            <div key={doc.id} className="rounded-lg border border-border p-3 bg-card">
                              <div className="flex gap-3">
                                <button
                                  onClick={() => setPreviewDoc(doc)}
                                  className="shrink-0 h-20 w-20 overflow-hidden rounded-md border border-border hover:opacity-80 bg-muted flex items-center justify-center"
                                >
                                  {doc.tipo === "image" ? (
                                    <img
                                      src={getFileUrl(doc)}
                                      alt={doc.campo}
                                      className="h-full w-full object-cover"
                                    />
                                  ) : doc.tipo === "video" ? (
                                    <div className="flex h-full w-full items-center justify-center bg-slate-800 text-2xl text-white">
                                      ▶
                                    </div>
                                  ) : (
                                    <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                                      PDF
                                    </div>
                                  )}
                                </button>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-foreground truncate">
                                    {docLabels[doc.campo] || humanizeKey(doc.campo)}
                                  </p>
                                  <span className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase ${statusColor}`}>
                                    {doc.status === "pendente" && doc.hasRejectedHistory
                                      ? "Reanálise"
                                      : doc.status}
                                  </span>
                                  {doc.motivoReprovacao && (
                                    <p className="mt-1 text-xs text-red-600 truncate" title={doc.motivoReprovacao}>
                                      {doc.motivoReprovacao}
                                    </p>
                                  )}
                                  {doc.hasRejectedHistory && doc.versao > 1 && (
                                    <p className="text-[10px] text-muted-foreground mt-0.5">
                                      versao {doc.versao}
                                    </p>
                                  )}
                                </div>
                              </div>
                              {doc.status === "pendente" && (
                                <div className="mt-2 flex gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => handleApproveDoc(doc.id)}
                                    disabled={docActionLoading}
                                    className="flex-1 bg-green-600 hover:bg-green-700 text-white h-8 text-xs"
                                  >
                                    Aprovar
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => { setRejectDoc(doc); setRejectDocMotivo(""); }}
                                    disabled={docActionLoading}
                                    className="flex-1 border-red-300 text-red-600 hover:bg-red-50 h-8 text-xs"
                                  >
                                    Reprovar
                                  </Button>
                                </div>
                              )}
                              {doc.status === "aprovado" && (
                                <div className="mt-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleUnapproveDoc(doc.id)}
                                    disabled={docActionLoading}
                                    className="w-full border-amber-300 text-amber-700 hover:bg-amber-50 h-8 text-xs"
                                  >
                                    Desfazer aprovação
                                  </Button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  {getActionsForStatus(detailItem).map((a, i) => (
                    <Button
                      key={i}
                      variant="outline"
                      className={toneClass(a.tone)}
                      onClick={a.onClick}
                    >
                      {a.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Reject Modal */}
      {rejectItem && (
        <Dialog
          open={!!rejectItem}
          onOpenChange={() => setRejectItem(null)}
          dirty={rejectMotivo.trim() !== ""}
          onSave={async () => { await handleReject(); }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Recusar Solicitacao</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                Cliente: <strong>{rejectItem.clienteNome}</strong> - {formatCurrency(rejectItem.valor)}
              </p>
              <div>
                <label className="mb-1 block text-sm font-medium">Motivo da recusa *</label>
                <textarea
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows={3}
                  value={rejectMotivo}
                  onChange={(e) => setRejectMotivo(e.target.value)}
                  placeholder="Descreva o motivo..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectItem(null)}>
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={rejecting || !rejectMotivo.trim()}
              >
                {rejecting ? "Recusando..." : "Confirmar Recusa"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Block Client Modal */}
      {blockItem && (
        <Dialog
          open={!!blockItem}
          onOpenChange={() => setBlockItem(null)}
          dirty={blockMotivo.trim() !== ""}
          onSave={async () => { await handleBlock(); }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("pipeline.block.title")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                {t("pipeline.block.description")}{" "}
                <strong>{blockItem.clienteNome}</strong>?
              </p>
              <p className="text-xs text-muted-foreground">
                {t("pipeline.block.consequence")}
              </p>
              <div>
                <label className="mb-1 block text-sm font-medium">
                  {t("pipeline.block.reason_label")}
                </label>
                <textarea
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows={3}
                  value={blockMotivo}
                  onChange={(e) => setBlockMotivo(e.target.value)}
                  placeholder={t("pipeline.block.reason_placeholder")}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBlockItem(null)}>
                {t("common.cancel")}
              </Button>
              <Button
                variant="destructive"
                onClick={handleBlock}
                disabled={blocking}
              >
                {blocking ? t("pipeline.block.blocking") : t("pipeline.block.confirm")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Confirm PIX Modal */}
      {pixItem && (
        <Dialog
          open={!!pixItem}
          onOpenChange={() => setPixItem(null)}
          dirty={pixDate !== pixInitialDateRef.current}
          onSave={async () => { await handleConfirmPix(); }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmar PIX e Criar Cobranca</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="rounded-lg bg-muted p-3 text-sm">
                <p><strong>Cliente:</strong> {pixItem.clienteNome}</p>
                <p><strong>Valor:</strong> {formatCurrency(pixItem.valor)}</p>
                <p><strong>Plano:</strong> {pixItem.parcelas}x {FREQ_LABELS[pixItem.frequencia] || pixItem.frequencia} ({pixItem.taxaJuros}% juros)</p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Data 1o Vencimento *</label>
                <Input
                  type="date"
                  value={pixDate}
                  onChange={(e) => setPixDate(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPixItem(null)}>
                Cancelar
              </Button>
              <Button onClick={handleConfirmPix} disabled={confirmingPix || !pixDate}>
                {confirmingPix ? "Criando..." : "Confirmar e Criar Cobranca"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Contract Preview Modal */}
      {contractPreviewItem && (
        <Dialog open={!!contractPreviewItem} onOpenChange={handleCloseContractPreview}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>{t("pipeline.preview.title")}</DialogTitle>
              <p className="text-sm text-muted-foreground">
                {contractPreviewItem.clienteNome} - {formatCurrency(contractPreviewItem.valor)}
              </p>
            </DialogHeader>
            <div className="min-h-[60vh] py-4">
              {contractPreviewLoading && (
                <div className="flex h-[60vh] items-center justify-center">
                  <div className="text-center">
                    <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
                    <p className="text-sm text-muted-foreground">
                      {t("pipeline.preview.loading")}
                    </p>
                  </div>
                </div>
              )}
              {!contractPreviewLoading && contractPreviewPdf && (
                <iframe
                  src={`data:application/pdf;base64,${contractPreviewPdf}`}
                  className="h-[60vh] w-full rounded border border-border"
                  title={t("pipeline.preview.title")}
                />
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseContractPreview}>
                {t("common.cancel")}
              </Button>
              <Button
                variant="outline"
                onClick={() => loadContractPreview(contractPreviewItem.id, true)}
                disabled={contractPreviewLoading || contractSending}
              >
                {t("pipeline.preview.regenerate")}
              </Button>
              <Button
                onClick={handleConfirmSendContract}
                disabled={
                  contractPreviewLoading || contractSending || !contractPreviewId
                }
              >
                {contractSending ? t("pipeline.preview.sending") : t("pipeline.preview.confirm_send")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Resend Contract Modal */}
      {resendItem && (
        <Dialog
          open={!!resendItem}
          onOpenChange={(open) => { if (!open) setResendItem(null); }}
          dirty={
            resendChannel !== resendInitialRef.current.channel ||
            resendMessage !== resendInitialRef.current.message
          }
          onSave={async () => { await handleConfirmResend(); }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t("pipeline.resend.title")}</DialogTitle>
              <p className="text-sm text-muted-foreground">
                {resendItem.clienteNome} - {formatCurrency(resendItem.valor)}
              </p>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t("pipeline.resend.channel_label")}
                </label>
                <div className="flex gap-2">
                  {(["WHATSAPP", "EMAIL"] as const).map((ch) => (
                    <button
                      key={ch}
                      type="button"
                      onClick={() => setResendChannel(ch)}
                      className={`flex-1 rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                        resendChannel === ch
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border text-foreground hover:bg-muted"
                      }`}
                    >
                      {ch === "WHATSAPP" ? "WhatsApp" : "E-mail"}
                    </button>
                  ))}
                </div>
                {(() => {
                  const destino = resendChannel === "WHATSAPP"
                    ? (resendItem.clienteWhatsapp || resendItem.clienteTelefone || "")
                    : "";
                  if (resendChannel === "WHATSAPP") {
                    return destino ? (
                      <div className="mt-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-sm">
                        <span className="text-muted-foreground">
                          {t("pipeline.resend.will_send_to")}{" "}
                        </span>
                        <strong className="font-mono text-foreground">{destino}</strong>
                      </div>
                    ) : (
                      <p className="mt-2 text-xs text-destructive">
                        {t("pipeline.resend.no_whatsapp")}
                      </p>
                    );
                  }
                  return (
                    <p className="mt-2 text-xs text-muted-foreground">
                      {t("pipeline.resend.email_not_supported")}
                    </p>
                  );
                })()}
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t("pipeline.resend.message_label")}
                </label>
                <textarea
                  value={resendMessage}
                  onChange={(e) => setResendMessage(e.target.value)}
                  placeholder={t("pipeline.resend.message_placeholder")}
                  rows={3}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("pipeline.resend.message_hint")}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResendItem(null)} disabled={resending}>
                {t("common.cancel")}
              </Button>
              <Button onClick={handleConfirmResend} disabled={resending}>
                {resending ? t("pipeline.resend.sending") : t("pipeline.resend.confirm")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Score modal — reusa @payflow/ui/ScoreDetailsDialog (mesmo componente
          da LoansPage). Layout espelha o ScoreGauge do mobile. Ver
          memoria/project_score_cobanq.md pras regras oficiais. */}
      <ScoreDetailsDialog
        open={!!scoreItem}
        onOpenChange={(o) => { if (!o) { setScoreItem(null); setScoreDetails(null); } }}
        cpf={scoreItem?.clienteCpf}
        data={scoreDetails}
        loading={scoreLoading}
        headerSubtitle={scoreItem ? `${scoreItem.clienteNome} · ${formatCpf(scoreItem.clienteCpf)}` : undefined}
        labels={{
          title: "Score CobanQ",
          punctualityTitle: "Pontualidade",
          punctualityStats: (paid, total) => `${paid} pagas / ${total} vencidas`,
          historyTitle: "Histórico na rede",
          historyEmpty: "Cliente sem empréstimos anteriores na rede CobanQ.",
          historyLoans: "Empréstimos",
          historyPaidOff: "Quitados",
          historyPaidInstallments: "Parcelas pagas",
          historyOverdueInstallments: "Parcelas em atraso",
          historyBiggestDelay: "Maior atraso",
          historyBiggestDelayValue: (days) => `${days} ${days === 1 ? "dia" : "dias"}`,
          historyRenegotiations: "Renegociações",
          historyPartners: "Parceiros que já atenderam",
          disclaimer: "Score CobanQ calculado a partir do histórico de pagamentos do cliente.",
        }}
      />

      {/* Document Preview Modal */}
      {previewDoc && (
        <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>{docLabels[previewDoc.campo] || humanizeKey(previewDoc.campo)}</DialogTitle>
            </DialogHeader>
            <div className="flex items-center justify-center p-2">
              {previewDoc.tipo === "image" ? (
                <img
                  src={getFileUrl(previewDoc)}
                  alt={previewDoc.campo}
                  className="max-h-[70vh] rounded-lg object-contain"
                />
              ) : previewDoc.tipo === "video" ? (
                <video
                  src={getFileUrl(previewDoc)}
                  controls
                  autoPlay
                  className="max-h-[70vh] w-full rounded-lg bg-black"
                />
              ) : (
                <iframe
                  src={getFileUrl(previewDoc)}
                  className="h-[70vh] w-full rounded-lg border"
                  title={previewDoc.campo}
                />
              )}
            </div>
            {previewDoc.status === "pendente" && (
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => { setRejectDoc(previewDoc); setRejectDocMotivo(""); setPreviewDoc(null); }}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  Reprovar
                </Button>
                <Button
                  onClick={() => { handleApproveDoc(previewDoc.id); setPreviewDoc(null); }}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  Aprovar
                </Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Reject Document Modal */}
      {rejectDoc && (
        <Dialog
          open={!!rejectDoc}
          onOpenChange={() => setRejectDoc(null)}
          dirty={rejectDocMotivo.trim() !== ""}
          onSave={async () => { await handleRejectDoc(); }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reprovar Documento</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                Documento: <strong>{rejectDoc.campo}</strong>
              </p>
              <div>
                <label className="mb-1 block text-sm font-medium">Motivo da reprovacao *</label>
                <textarea
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows={3}
                  value={rejectDocMotivo}
                  onChange={(e) => setRejectDocMotivo(e.target.value)}
                  placeholder="Explique o motivo para o cliente poder reenviar"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectDoc(null)}>
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleRejectDoc}
                disabled={docActionLoading || !rejectDocMotivo.trim()}
              >
                {docActionLoading ? "Reprovando..." : "Confirmar Reprovacao"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// Small check icon
/**
 * Icone do WhatsApp — SVG mesmo path oficial (verde #25D366).
 */
function WhatsAppIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

/**
 * Link clicavel pro WhatsApp com icone. Normaliza pra digitos e prefixa 55
 * (BR) se nao tiver country code. Fallback pra "-" quando nao ha numero.
 */
function WhatsAppLink({ phone, showRaw = true }: { phone?: string | null; showRaw?: boolean }) {
  const digits = (phone || "").replace(/\D/g, "");
  if (!digits) return <span className="font-mono text-xs text-muted-foreground">-</span>;
  const withCountry = digits.length <= 11 ? `55${digits}` : digits;
  return (
    <a
      href={`https://wa.me/${withCountry}`}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-mono text-green-700 hover:bg-green-50"
      title="Abrir WhatsApp"
    >
      <WhatsAppIcon className="h-4 w-4 text-green-600" />
      {showRaw && <span>{phone}</span>}
    </a>
  );
}

/**
 * Field com label + link WhatsApp quando ha telefone. Usado em Contato do
 * modal de detalhe. Fallback "-" quando vazio.
 */
function PhoneField({ label, phone }: { label: string; phone?: string | null }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      {phone ? (
        <WhatsAppLink phone={phone} />
      ) : (
        <span className="text-sm font-bold">-</span>
      )}
    </div>
  );
}

// Score components + mockScore/mockScoreDetails/classifyScore ficam em @payflow/ui.

/**
 * Field key/value do card "Dados do Cliente". Label em cinza pequeno,
 * value bold; `size` controla o peso do value (lg pra Valor/Parcelas,
 * xl pra Juros).
 */
function Field({
  label,
  value,
  size = "md",
  valueClassName = "",
}: {
  label: string;
  value: string;
  size?: "md" | "lg" | "xl";
  valueClassName?: string;
}) {
  const valueSize =
    size === "xl" ? "text-3xl font-bold" : size === "lg" ? "text-lg font-bold" : "text-sm font-bold";
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className={`${valueSize} ${valueClassName}`}>{value}</span>
    </div>
  );
}
